import React, { useReducer, useEffect, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Tenant, TenantContextType } from '../types/tenant';
import { TENANT_STORAGE_KEY } from '../config/tenants';
import { loadStoredTenant } from './tenantUtils';
import { TenantContext } from './tenantContext';
import { useTenantsService } from '../services/tenantsService';
import { Box, CircularProgress, Typography, Paper, Alert, Button } from '@mui/material';
import { Warning, Refresh, Logout } from '@mui/icons-material';

// Tipos para o reducer
interface TenantState {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  isLoading: boolean;
}

type TenantAction =
  | { type: 'SET_CURRENT_TENANT'; payload: Tenant | null }
  | { type: 'SET_AVAILABLE_TENANTS'; payload: Tenant[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_TENANT' };

// Estado inicial
const initialState: TenantState = {
  currentTenant: null,
  availableTenants: [],
  isLoading: true, // Inicia como true para carregar os tenants da API
};

// Reducer para gerenciar o estado
const tenantReducer = (state: TenantState, action: TenantAction): TenantState => {
  switch (action.type) {
    case 'SET_CURRENT_TENANT':
      return {
        ...state,
        currentTenant: action.payload,
      };
    case 'SET_AVAILABLE_TENANTS':
      return {
        ...state,
        availableTenants: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'CLEAR_TENANT':
      return {
        ...state,
        currentTenant: null,
      };
    default:
      return state;
  }
};



// Provider do contexto
interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(tenantReducer, initialState);
  const { logout } = useAuth0();
  const tenantsService = useTenantsService();

  // Carregar tenants da API e tenant do localStorage na inicialização
  useEffect(() => {
    const initializeTenants = async () => {
      try {
        // Carregar tenants da API
        const tenants = await tenantsService.getTenants();
        dispatch({ type: 'SET_AVAILABLE_TENANTS', payload: tenants });
        
        // Carregar tenant salvo do localStorage
        loadStoredTenant(dispatch, tenants);
      } catch (error) {
        console.error('Erro ao inicializar tenants:', error);
        // Em caso de erro, manter loading como false para não travar a aplicação
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeTenants();
  }, []);

  // Função para selecionar um tenant
  const selectTenant = (tenantName: string) => {
    const tenant = state.availableTenants.find(t => t.name === tenantName);
    if (tenant) {
      dispatch({ type: 'SET_CURRENT_TENANT', payload: tenant });
      localStorage.setItem(TENANT_STORAGE_KEY, tenantName);
    } else {
      console.error(`Tenant com nome '${tenantName}' não encontrado`);
    }
  };

  // Função para limpar a seleção de tenant
  const clearTenant = () => {
    dispatch({ type: 'CLEAR_TENANT' });
    localStorage.removeItem(TENANT_STORAGE_KEY);
  };

  // Função para recarregar tenants da API
  const loadTenants = async (): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const tenants = await tenantsService.getTenants();
      dispatch({ type: 'SET_AVAILABLE_TENANTS', payload: tenants });
    } catch (error) {
      console.error('Erro ao carregar tenants:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const contextValue: TenantContextType = {
    currentTenant: state.currentTenant,
    availableTenants: state.availableTenants,
    selectTenant,
    clearTenant,
    isTenantSelected: state.currentTenant !== null,
    loadTenants,
    isLoading: state.isLoading,
  };

  // Se estiver carregando, mostrar tela de loading que bloqueia a aplicação
  if (state.isLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Carregando tenants...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Aguarde enquanto buscamos os tenants disponíveis
        </Typography>
      </Box>
    );
  }

  // Se não há tenants disponíveis, mostrar tela de erro
  if (!state.isLoading && state.availableTenants.length === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: 600,
            padding: 4,
            textAlign: 'center',
          }}
        >
          <Warning sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
          
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Sem Acesso a Tenants
          </Typography>
          
          <Alert severity="warning" sx={{ my: 3, textAlign: 'left' }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Você não possui acesso a nenhum tenant.</strong>
            </Typography>
            <Typography variant="body2">
              Isso pode acontecer porque:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, textAlign: 'left' }}>
              <li>Seu usuário ainda não foi configurado em nenhum tenant</li>
              <li>Suas permissões ainda estão sendo processadas</li>
              <li>Houve um erro ao carregar seus tenants</li>
            </Box>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Entre em contato com o administrador do sistema para obter acesso ou tente recarregar a página.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={() => window.location.reload()}
            >
              Recarregar Página
            </Button>
            <Button
              variant="outlined"
              startIcon={<Logout />}
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              color="error"
            >
              Sair
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};
